/**
 * @fileoverview `useCaptchaTranslate(t)` — the captcha corpus's translator.
 * @package @puffin-core/captcha-ui
 *
 * AAS-I18N-002 W8 / user ruling 25 (2026-08-10). Deliberately shaped after
 * `@puffin-core/login-shell`'s `useLoginTranslate` so the two shared-package
 * corpora behave identically for a reader and for a future maintainer.
 *
 * ## Resolution order
 *
 *   1. a consumer-supplied `t` prop — honoured first, so any caller that already
 *      threads a translator keeps control;
 *   2. the `captcha` i18next namespace on the instance in the React tree —
 *      registered ONCE at app boot by `@puffin-core/i18n-namespaces`, before
 *      first paint;
 *   3. `DEFAULT_CAPTCHA_STRINGS` — the English degrade path, byte-identical to
 *      the behaviour before this hook existed.
 *
 * Step 3 matters as much as step 2: a tree with NO i18n provider at all (several
 * package suites render `<CaptchaField>` bare) must render readable English, not
 * a raw dotted key. react-i18next returns the key itself for a missing lookup,
 * which is exactly the shape `isUsable` rejects.
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { DEFAULT_CAPTCHA_STRINGS } from './strings/en.js';
import { CAPTCHA_NAMESPACE } from './strings/namespace.js';

/**
 * A translator result is "usable" unless it is one of the shapes that mean
 * *missing*: nullish, empty, or the key echoed back.
 *
 * @param {unknown} value
 * @param {string} key
 * @returns {boolean}
 */
function isUsable(value, key) {
  return value !== undefined && value !== null && value !== '' && value !== key;
}

/**
 * Build the captcha corpus translator.
 *
 * @param {((key: string) => string|undefined|null)} [consumerT] Optional caller
 *   translator, kept first in the resolution order.
 * @returns {(key: string) => string} A translator that never returns a raw key.
 */
export function useCaptchaTranslate(consumerT) {
  const { t: namespaceT } = useTranslation(CAPTCHA_NAMESPACE);

  // 🔴 The render-phase `ensureCaptchaStrings(i18n)` call that used to sit here was DELETED by
  // I18N-RENDER-002 (ruling R-2, 2026-08-22), after a census — not on assumption, which R-2
  // explicitly forbade.
  //
  // Why it existed: the sibling loaders were wired by hand in 20 `createAppI18n` call sites, and
  // `<CaptchaField>` renders far outside login flows (password-reset modals, public contact forms,
  // the signup wizard), so an app could legitimately mount it without ever thinking about
  // login-shell. Self-registration during render was the honest answer to "a binding seam nobody
  // mounts does not exist" — and it had to be render-phase, because react-i18next does not
  // re-render on an `added` resource event, so an effect would leave the first paint English.
  //
  // Why it can go: `@puffin-core/i18n-namespaces` now registers `captcha` for EVERY app, from BOTH
  // registries (`APP_I18N_NAMESPACES` and `MARKETING_I18N_NAMESPACES`), inside `createAppI18n`
  // immediately after `init()` — before first paint. The hand-wiring the side effect compensated
  // for no longer exists.
  //
  // MEASURED before deleting: every non-test `<CaptchaField>` render site in `apps/` and
  // `packages/` resolves to a tree booted by one of those two registries; the four `createAppI18n`
  // call sites that name NEITHER registry (three error boundaries and a preferences preview) render
  // zero captchas. `captchaRegistryCoverage.contract.test.js` is the standing guard for that fact —
  // it is what makes this deletion safe rather than merely true today.
  //
  // Step 3 of the resolution order (`DEFAULT_CAPTCHA_STRINGS`) is untouched, so a tree with NO
  // provider still renders readable English rather than a raw dotted key.

  return useCallback(
    (key) => {
      if (typeof consumerT === 'function') {
        const fromConsumer = consumerT(key);
        if (isUsable(fromConsumer, key)) {
          return fromConsumer;
        }
      }
      // `useTranslation` ALWAYS returns a callable `t` — even with no provider in
      // the tree, where it binds react-i18next's default instance. A
      // `typeof namespaceT === 'function'` guard here measured as an unreachable
      // branch (Gen-0: no published contract promises the non-function case).
      const fromNamespace = namespaceT(key);
      if (isUsable(fromNamespace, key)) {
        return fromNamespace;
      }
      return DEFAULT_CAPTCHA_STRINGS[key];
    },
    [consumerT, namespaceT],
  );
}

export default useCaptchaTranslate;
